// Drop announcement switch: swaps the "next drop" copy for the "just dropped"
// copy at a fixed moment (switch-at, ISO 8601 with offset). Liquid renders the
// right phase on page load; this element corrects cached pages, ticks the live
// countdown, and flips the copy live when it hits zero.

class DropSwitch extends HTMLElement {
  #interval = 0;
  #switchAt = NaN;
  #switched = false;

  connectedCallback() {
    this.#switchAt = Date.parse(this.getAttribute('switch-at') || '');
    if (Number.isNaN(this.#switchAt)) return;

    this.#showLocalTime(this.#switchAt);

    if (this.#switchAt - Date.now() <= 0) {
      this.#show('after');
      return;
    }

    this.#show('before');
    const countdown = /** @type {HTMLElement | null} */ (this.querySelector('[data-drop-countdown]'));
    if (countdown) countdown.hidden = false;
    this.#tick();
    this.#interval = window.setInterval(() => this.#tick(), 1000);
  }

  disconnectedCallback() {
    window.clearInterval(this.#interval);
  }

  #tick() {
    const remaining = Math.max(0, this.#switchAt - Date.now());
    const total = Math.ceil(remaining / 1000);
    const parts = {
      days: Math.floor(total / 86400),
      hours: Math.floor((total % 86400) / 3600),
      minutes: Math.floor((total % 3600) / 60),
      seconds: total % 60,
    };
    for (const [unit, value] of Object.entries(parts)) {
      const el = this.querySelector(`[data-unit="${unit}"]`);
      if (el) el.textContent = String(value).padStart(2, '0');
    }
    if (total <= 0 && !this.#switched) {
      this.#switched = true;
      window.clearInterval(this.#interval);
      this.#show('after');
    }
  }

  /**
   * "Your time: Fri, Oct 23, 8:00 AM EDT": the drop moment in the visitor's own
   * time zone and language. Skipped for visitors whose clock is already on JST,
   * since the headline date says the same thing.
   * @param {number} switchAt
   */
  #showLocalTime(switchAt) {
    const line = /** @type {HTMLElement | null} */ (this.querySelector('[data-drop-local]'));
    const slot = this.querySelector('[data-drop-local-time]');
    if (!line || !slot) return;
    try {
      const zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      if (zone === 'Asia/Tokyo' || new Date(switchAt).getTimezoneOffset() === -540) return;
      slot.textContent = new Intl.DateTimeFormat(document.documentElement.lang || undefined, {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        timeZoneName: 'short',
      }).format(new Date(switchAt));
      line.hidden = false;
    } catch {
      // Leave the line hidden; the fixed city times below still apply.
    }
  }

  #show(phase) {
    this.dataset.phase = phase;
    for (const el of this.querySelectorAll('[data-drop-phase]')) {
      el.hidden = el.dataset.dropPhase !== phase;
    }
  }
}

if (!customElements.get('s-drop-switch')) {
  customElements.define('s-drop-switch', DropSwitch);
}
